import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { CalendarClock, Info, Download, MapPin, X } from 'lucide-react';
import { contentAPI, dynamicAPI } from '../services/api';
import { difficultyDotTier, normalizeTrekDifficultyLabel } from '../constants/trekDifficulty.js';
import { pushDocumentScrollLock, popDocumentScrollLock } from '../utils/documentScrollLock.js';
import { downloadBrochureFromUrl, openBrochurePlaceholderWindow, resolveBrochureCdnUrl } from '../utils/downloadBrochureFromUrl.js';
import { resolveImageDisplayUrls, imageLoadErrorFallback, IMAGE_DISPLAY_WIDTH } from '../utils/optimizeImageUrl.js';
import './EventCard.css';

const FALLBACK_EVENT_IMAGE = 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=400';
const MOBILE_DIGITS = 10;

const normalizeMobile = (value) => String(value || '').replace(/\D/g, '').slice(0, MOBILE_DIGITS);
const isValidMobile = (value) => normalizeMobile(value).length === MOBILE_DIGITS;

const EventCard = ({ event }) => {
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfForm, setPdfForm] = useState({ name: '', mobile: '' });
  const [pdfSubmitting, setPdfSubmitting] = useState(false);
  const [pdfError, setPdfError] = useState('');

  const openPdfModal = () => {
    setPdfModalOpen(true);
    setPdfError('');
    setPdfForm({ name: '', mobile: '' });
  };

  const closePdfModal = () => {
    if (pdfSubmitting) return;
    setPdfModalOpen(false);
    setPdfError('');
  };

  useEffect(() => {
    if (!pdfModalOpen) return;
    pushDocumentScrollLock();
    return () => popDocumentScrollLock();
  }, [pdfModalOpen]);

  const handlePdfSubmit = async (e) => {
    e.preventDefault();
    setPdfError('');
    if (!pdfForm.name.trim()) {
      setPdfError('Name is required.');
      return;
    }
    if (!isValidMobile(pdfForm.mobile)) {
      setPdfError('Please enter a valid 10-digit mobile number.');
      return;
    }
    const url = event.brochureUrl ? String(event.brochureUrl).trim() : '';
    const cdnUrl = resolveBrochureCdnUrl(url);
    // Open tab during the click gesture — browsers block this after await.
    const popup = cdnUrl ? openBrochurePlaceholderWindow() : null;
    setPdfSubmitting(true);
    try {
      await contentAPI.submitPdfRequest({
        name: pdfForm.name.trim(),
        mobile: normalizeMobile(pdfForm.mobile),
        eventSlug: event.slug || null,
        eventTitle: event.title || null
      });
      setPdfModalOpen(false);
      setPdfForm({ name: '', mobile: '' });
      if (cdnUrl) {
        await downloadBrochureFromUrl(url, { name: event.title, popup });
      } else {
        if (popup && !popup.closed) {
          try {
            popup.close();
          } catch {
            /* ignore */
          }
        }
        window.alert(
          "Thanks! A brochure PDF isn't uploaded for this trek yet — our team will share it with you shortly."
        );
      }
    } catch (err) {
      if (popup && !popup.closed) {
        try {
          popup.close();
        } catch {
          /* ignore */
        }
      }
      setPdfError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setPdfSubmitting(false);
    }
  };

  // CDN-rewrite media[]; slide track for scroll transition. Only hydrate nearby slides so we
  // don't fetch every multi-MB gallery photo at once.
  const mediaDisplay = useMemo(() => {
    const rawList =
      Array.isArray(event.media) && event.media.length
        ? event.media.map((m) => m?.url).filter(Boolean)
        : [];
    const urls = rawList.length
      ? rawList
      : [event.imageUrl || FALLBACK_EVENT_IMAGE];
    return urls.map((url) =>
      resolveImageDisplayUrls(url || FALLBACK_EVENT_IMAGE, { width: IMAGE_DISPLAY_WIDTH.card })
    );
  }, [event.media, event.imageUrl]);

  const [heroIndex, setHeroIndex] = useState(0);
  const [loadedSlideIndexes, setLoadedSlideIndexes] = useState(() => new Set([0]));
  const mediaRef = useRef(null);
  /** Pixel width of .event-card-media — translate matches slide width (no next-slide peek). */
  const [mediaViewportPx, setMediaViewportPx] = useState(0);

  useEffect(() => {
    setHeroIndex(0);
    setLoadedSlideIndexes(new Set([0]));
  }, [event?.id, event?.slug, mediaDisplay.length]);

  useEffect(() => {
    setLoadedSlideIndexes((prev) => {
      const next = new Set(prev);
      const n = mediaDisplay.length;
      if (!n) return next;
      next.add(heroIndex);
      next.add((heroIndex + 1) % n);
      next.add((heroIndex - 1 + n) % n);
      return next;
    });
  }, [heroIndex, mediaDisplay.length]);

  useLayoutEffect(() => {
    if (mediaDisplay.length <= 1) {
      setMediaViewportPx(0);
      return;
    }
    const el = mediaRef.current;
    if (!el) return;
    const w = el.clientWidth;
    if (w > 0) setMediaViewportPx(w);
  }, [mediaDisplay.length, event?.slug, event?.id]);

  useEffect(() => {
    if (mediaDisplay.length <= 1) {
      setMediaViewportPx(0);
      return undefined;
    }
    const el = mediaRef.current;
    if (!el) return undefined;

    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setMediaViewportPx(w);
    };

    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('orientationchange', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', update);
    };
  }, [mediaDisplay.length, event?.slug, event?.id]);

  // Auto-rotate with horizontal scroll (track translate), not src swap.
  useEffect(() => {
    if (mediaDisplay.length <= 1) return undefined;
    const interval = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % mediaDisplay.length);
    }, 1500);
    return () => clearInterval(interval);
  }, [mediaDisplay.length]);

  const safeHeroIndex =
    mediaDisplay.length > 0 ? Math.min(heroIndex, mediaDisplay.length - 1) : 0;
  const durationLabel = event.duration || 'TBA';
  const locationLabel = event.location || event.region || event.baseCamp || '';
  const difficultyLabel = normalizeTrekDifficultyLabel(
    event.difficulty || event.difficultyLevel || event.level || event.grade || 'Moderate+'
  );

  // Build departure chips from one or more dates.
  // Prefer event.departureDates (array), otherwise fall back to startDate.
  const rawDates = Array.isArray(event.departureDates) && event.departureDates.length
    ? event.departureDates
    : (event.startDate ? [event.startDate] : []);

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const todayLocalStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const parsedDates = rawDates
    .map((value) => {
      if (!value) return null;
      const dateOnly = String(value).split('T')[0]; // YYYY-MM-DD
      const parts = dateOnly.split('-');
      if (parts.length !== 3) return null;
      const [yy, mm, dd] = parts;
      const year = Number(yy);
      const monthIndex = Number(mm) - 1;
      const dayNum = Number(dd);
      if (Number.isNaN(year) || Number.isNaN(monthIndex) || Number.isNaN(dayNum) || !MONTHS[monthIndex]) {
        return null;
      }
      return {
        year,
        monthIndex,
        yearMonthKey: `${yy}-${mm}`,
        dayNum,
        dateStr: dateOnly
      };
    })
    .filter(Boolean)
    .filter((p) => p.dateStr >= todayLocalStr)
    .sort((a, b) => a.dateStr.localeCompare(b.dateStr));

  const groupedByYearMonth = parsedDates.reduce((acc, { yearMonthKey, monthIndex, year, dayNum, dateStr }) => {
    if (!acc[yearMonthKey]) acc[yearMonthKey] = { monthIndex, year, days: [] };
    acc[yearMonthKey].days.push({ dayNum, dateStr });
    return acc;
  }, {});

  const firstDateStr = parsedDates[0]?.dateStr ?? null;
  const [selectedDateStr, setSelectedDateStr] = useState(firstDateStr);
  const dateStrList = parsedDates.map((p) => p.dateStr).join(',');
  useEffect(() => {
    setSelectedDateStr((prev) => (parsedDates.some((p) => p.dateStr === prev) ? prev : firstDateStr));
  }, [event?.id, firstDateStr, dateStrList]);

  const yearMonthKeys = Object.keys(groupedByYearMonth).sort((a, b) => a.localeCompare(b));

  const departureMonthLabel = (yearMonthKey) => {
    const [year, mm] = yearMonthKey.split('-');
    const monthIndex = Number(mm) - 1;
    const name = MONTHS[monthIndex] || '';
    const years = new Set(yearMonthKeys.map((k) => k.split('-')[0]));
    if (years.size <= 1) return name;
    if (yearMonthKeys.filter((k) => k.split('-')[1] === mm).length > 1) {
      return `${name} ${year}`;
    }
    const firstYear = yearMonthKeys[0]?.split('-')[0];
    if (year !== firstYear) return `${name} ${year}`;
    return name;
  };

  const [activeYearMonth, setActiveYearMonth] = useState(yearMonthKeys[0] ?? null);

  const activeYearMonthKey = yearMonthKeys.includes(activeYearMonth) ? activeYearMonth : yearMonthKeys[0];

  const rawPrice =
    event.price ??
    event.startingPrice ??
    event.starting_from ??
    event.startingFrom ??
    event.packages?.find?.((pkg) => pkg?.isMain)?.price ??
    event.packages?.[0]?.price ??
    null;

  const normalizedPrice = Number(rawPrice);
  const priceLabel =
    Number.isFinite(normalizedPrice) && normalizedPrice > 0
      ? `₹${normalizedPrice.toLocaleString('en-IN')}`
      : null;

  const tier = difficultyDotTier(difficultyLabel);
  const difficultyBars = tier > 0 ? tier : 1;

  const slugForRoute =
    event.slug != null && String(event.slug).trim() !== '' ? String(event.slug).trim() : '';
  const fallbackSegment =
    slugForRoute || (event.id != null && String(event.id).trim() !== '' ? String(event.id) : '');
  /*
   * Public URL should be slug-based (old site behavior). We keep a numeric fallback
   * only when no slug is available (e.g. legacy/bad data).
   */
  const pathSegment = slugForRoute || fallbackSegment;
  const dateQs = selectedDateStr ? `?date=${encodeURIComponent(selectedDateStr)}` : '';
  const detailTo = pathSegment ? `/events/${encodeURIComponent(pathSegment)}${dateQs}` : '/events';

  const onNavigateDetails = () => {
    const tid = event.trekId ?? event.trek_id;
    if (tid != null && tid !== '') {
      dynamicAPI.recordTrekDetailClick(tid).catch(() => {});
    }
  };

  const pdfModal = pdfModalOpen ? (
    <div
      className="event-card-pdf-overlay"
      onClick={closePdfModal}
      role="dialog"
      aria-modal="true"
      aria-labelledby="pdf-modal-title"
    >
      <div
        className="event-card-pdf-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="event-card-pdf-modal-header">
          <h3 id="pdf-modal-title">Download Event PDF</h3>
          <button
            type="button"
            className="event-card-pdf-modal-close modal-close-circle"
            onClick={closePdfModal}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handlePdfSubmit} className="event-card-pdf-form">
          <label className="event-card-pdf-label">
            <span className="event-card-pdf-label-text">Name <span className="required">*</span></span>
            <input
              type="text"
              value={pdfForm.name}
              onChange={(e) => setPdfForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Your name"
              required
            />
          </label>
          <label className="event-card-pdf-label">
            <span className="event-card-pdf-label-text">Mobile Number <span className="required">*</span></span>
            <input
              type="tel"
              value={pdfForm.mobile}
              onChange={(e) => setPdfForm((f) => ({ ...f, mobile: normalizeMobile(e.target.value) }))}
              placeholder="10-digit mobile number"
              required
              inputMode="numeric"
              maxLength={MOBILE_DIGITS}
            />
          </label>
          <label className="event-card-pdf-label">
            <span className="event-card-pdf-label-text">Event</span>
            <input
              type="text"
              value={event.title || ''}
              readOnly
              disabled
              className="event-card-pdf-event-readonly"
            />
          </label>
          {pdfError && <p className="event-card-pdf-error">{pdfError}</p>}
          <button type="submit" className="event-card-pdf-submit" disabled={pdfSubmitting}>
            {pdfSubmitting ? 'Submitting...' : 'Download PDF'}
          </button>
        </form>
      </div>
    </div>
  ) : null;

  return (
    <>
      <article className="event-card-universal">
        <div className="event-card-media" ref={mediaRef}>
          <Link
            to={detailTo}
            className="event-card-media-link"
            onClick={onNavigateDetails}
            aria-label={event.title ? `View ${event.title}` : 'View event details'}
          >
            {mediaDisplay.length <= 1 ? (
              <img
                className="event-card-image"
                src={mediaDisplay[0]?.src || FALLBACK_EVENT_IMAGE}
                alt={event.title ? `${event.title} trek` : 'Trek event'}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  const next = imageLoadErrorFallback(
                    e.currentTarget.src,
                    mediaDisplay[0]?.fallbackSrc,
                    FALLBACK_EVENT_IMAGE
                  );
                  if (next === e.currentTarget.src) {
                    e.currentTarget.onerror = null;
                    return;
                  }
                  e.currentTarget.src = next;
                }}
              />
            ) : (
              <div
                className="event-card-media-track"
                style={{
                  transform:
                    mediaViewportPx > 0
                      ? `translate3d(-${safeHeroIndex * mediaViewportPx}px, 0, 0)`
                      : `translateX(-${safeHeroIndex * 100}%)`
                }}
              >
                {mediaDisplay.map((slide, idx) => {
                  const shouldLoad = loadedSlideIndexes.has(idx);
                  return (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={idx}
                      className="event-card-media-slide"
                      style={
                        mediaViewportPx > 0
                          ? {
                              flex: `0 0 ${mediaViewportPx}px`,
                              width: mediaViewportPx,
                              minWidth: mediaViewportPx,
                              maxWidth: mediaViewportPx
                            }
                          : undefined
                      }
                    >
                      {shouldLoad ? (
                        <img
                          className="event-card-image"
                          src={slide.src}
                          alt={event.title ? `${event.title} - view ${idx + 1}` : 'Trek event image'}
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            const next = imageLoadErrorFallback(
                              e.currentTarget.src,
                              slide.fallbackSrc,
                              FALLBACK_EVENT_IMAGE
                            );
                            if (next === e.currentTarget.src) {
                              e.currentTarget.onerror = null;
                              return;
                            }
                            e.currentTarget.src = next;
                          }}
                        />
                      ) : (
                        <div className="event-card-image event-card-image--placeholder" aria-hidden />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {event.badge && <div className="event-card-badge">{event.badge}</div>}
          </Link>
          {mediaDisplay.length > 1 ? (
            <div className="event-card-media-dots" role="tablist" aria-label="Event photos">
              {mediaDisplay.map((_, idx) => (
                <button
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  type="button"
                  role="tab"
                  aria-selected={idx === safeHeroIndex}
                  aria-label={`Photo ${idx + 1} of ${mediaDisplay.length}`}
                  className={`event-card-media-dot ${idx === safeHeroIndex ? 'active' : ''}`}
                  onClick={() => setHeroIndex(idx)}
                />
              ))}
            </div>
          ) : null}
        </div>

      <div className="event-card-content">
        <Link
          to={detailTo}
          className="event-card-main-link"
          onClick={onNavigateDetails}
          aria-label={event.title ? `View ${event.title}` : 'View event details'}
        >
          <div className="event-card-main">
            <div className="event-card-meta-row">
              <div className="event-meta-item">
                <CalendarClock size={16} aria-hidden />
                <span>{durationLabel}</span>
              </div>
              {locationLabel && (
                <div className="event-meta-item">
                  <MapPin size={16} aria-hidden />
                  <span>{locationLabel}</span>
                </div>
              )}
            </div>

            <div className="event-card-text">
              <h4 className="event-card-title-heading">
                <span className="event-card-title-text">{event.title}</span>
              </h4>
              <p>{event.shortDescription || 'Experience the adventure of a lifetime.'}</p>
            </div>

            {(priceLabel || difficultyLabel) && (
              <div className="event-card-stats">
                {priceLabel && (
                  <div className="event-card-price">
                    <span className="event-card-price-label">Starting From</span>
                    <span className="event-card-price-value">{priceLabel}</span>
                  </div>
                )}
                <div className="event-card-difficulty">
                  <span className="event-card-difficulty-label">Difficulty</span>
                  <div className="event-card-difficulty-bars" aria-hidden>
                    <span className={difficultyBars >= 1 ? 'active' : ''} />
                    <span className={difficultyBars >= 2 ? 'active' : ''} />
                    <span className={difficultyBars >= 3 ? 'active' : ''} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </Link>

        {yearMonthKeys.length > 0 ? (
          <div className="event-card-departures">
            <div className="event-card-months">
              {yearMonthKeys.map((yearMonthKey) => {
                const isActive = yearMonthKey === activeYearMonthKey;
                return (
                  <button
                    key={yearMonthKey}
                    type="button"
                    className={`event-card-month ${isActive ? 'active' : ''}`}
                    onClick={() => setActiveYearMonth(yearMonthKey)}
                  >
                    {departureMonthLabel(yearMonthKey)}
                  </button>
                );
              })}
            </div>
            <div className="event-card-days">
              {(groupedByYearMonth[activeYearMonthKey]?.days || [])
                .slice()
                .sort((a, b) => a.dayNum - b.dayNum)
                .map((day, idx) => (
                  <button
                    key={`${activeYearMonthKey}-${day.dateStr}-${idx}`}
                    type="button"
                    className={`event-card-day ${day.dateStr === selectedDateStr ? 'active' : ''}`}
                    onClick={() => setSelectedDateStr(day.dateStr)}
                  >
                    {String(day.dayNum).padStart(2, '0')}
                  </button>
                ))}
            </div>
          </div>
        ) : (
          <div
            className="event-card-departures event-card-departures--placeholder"
            aria-hidden="true"
          />
        )}

        <div className="event-card-actions">
          <Link to={detailTo} className="event-card-btn-details" onClick={onNavigateDetails}>
            <Info size={16} aria-hidden />
            More Details
          </Link>
          <button
            type="button"
            className="event-card-btn-pdf"
            onClick={openPdfModal}
          >
            <Download size={16} />
            Get PDF
          </button>
        </div>
      </div>
      </article>
      {pdfModal && ReactDOM.createPortal(pdfModal, document.body)}
    </>
  );
};

export default EventCard;
